import { Appointment, ERROR_CODE } from "@elixir/types";
import { notification } from "antd";
import dateFormat from "dateformat";
import { useSendAppointmentReminder } from "../../../apis/tryst/send-appointment-reminders";
import { useSendFollowupReminder } from "../../../apis/tryst/send-followup-reminders";
import { ReactComponent as BellRegularIcon } from "../../../assets/icons/font-awesome/bell-regular.svg";
import { ReactComponent as CalendarRegularIcon } from "../../../assets/icons/font-awesome/calendar-regular.svg";
import { ReactComponent as MessageRegularIcon } from "../../../assets/icons/font-awesome/message-regular.svg";
import { ReactComponent as UserRegularIcon } from "../../../assets/icons/font-awesome/user-regular.svg";
import IconButton from "../../../atoms/button/icon-button";
import { QUEUE_TYPE } from "../../../constants/appointments";
import { track } from "../../../utils/tracking";
import BookedDetailsCard from "./booked-details-card";
import { useMyAccessProfileV2 } from "../../../apis/tryst/get-my-access-profile";

const getReminderMessage = (
  patientName: string,
  doctorName: string,
  date: Date,
  queueType: QUEUE_TYPE
) => {
  if (queueType === QUEUE_TYPE.FOLLOWUP) {
    return (
      <div className="dr-text-doctor-darwin-neutral-900 HeadingsH2Regular rounded-4 dr-bg-darwin-touch-neutral-50 dr-p-16 space-y-16">
        <div>Message from {doctorName}</div>
        <div>Hi {patientName}.</div>
        <div>
          I would like to know if you would be coming for your follow up
          appointment on {dateFormat(date, "dS mmm'yy")}.
        </div>
        <div>
          • Tap {`'`}Yes{`'`} to confirm.
        </div>
        <div>
          • Tap {`'`}No{`'`} if you are not coming.
        </div>
      </div>
    );
  } else if (queueType === QUEUE_TYPE.BOOKED) {
    return (
      <div className="dr-text-doctor-darwin-neutral-900 HeadingsH2Regular rounded-4 dr-bg-darwin-touch-neutral-50 dr-p-16 space-y-16">
        <div>Message from {doctorName}</div>
        <div>Hi {patientName}.</div>
        <div>
          I would like to know if you would be coming for your appointment on{" "}
          {dateFormat(date, "DDDD, dS mmm'yy HH:MM TT")}.
        </div>
        <div>
          • Tap {`'`}Yes{`'`} to confirm.
        </div>
        <div>
          • Tap {`'`}No{`'`} if you are not coming.
        </div>
      </div>
    );
  }
};
const SendReminder = ({
  queueType,
  onClose,
  data,
  HeaderComponent,
  className = "",
}: {
  queueType: QUEUE_TYPE;
  onClose?: () => void;
  data: Appointment;
  showCloseIcon?: boolean;
  HeaderComponent?: () => JSX.Element;
  className?: string;
}) => {
  const { patient, comm_meta = [], clinic_id, start_time, doctor_oid } = data;
  const { data: accessProfile } = useMyAccessProfileV2();

  const doctorFullName = accessProfile?.doctors?.find(
    (item) => item.oid === doctor_oid
  )?.name;

  const { mutateAsync: sendAppointmentReminder, isLoading } =
    useSendAppointmentReminder();
  const { mutateAsync: sendFollowupReminder } = useSendFollowupReminder();

  const handleFollowupReminder = async () => {
    if (queueType === QUEUE_TYPE.FOLLOWUP) {
      const response = await sendFollowupReminder({ aid: data.aid });
      if (response.success) {
        notification.success({
          placement: "bottomRight",
          message: "Reminder Sent",
          description: `Follow-up reminder sent to ${patient.fln}.`,
        });
        onClose?.();
      } else {
        notification.error({
          placement: "bottomRight",
          message: "Error in sending follow up reminder",
          description: response.error,
        });
      }
    } else {
      track("send_reminder", {
        queueType,
      });
      const response = await sendAppointmentReminder(data);
      if (response.success) {
        if (
          response.error_meta &&
          response.error_meta.code === ERROR_CODE.LOW_CREDITS
        ) {
          notification.success({
            message: response.error_meta.message,
            description: `Appointment reminder sent to ${patient.fln}.`,
            placement: "bottomRight",
          });
        } else {
          notification.success({
            message: "Reminder Sent",
            description: `Appointment reminder sent to ${patient.fln}.`,
            placement: "bottomRight",
          });
        }
        onClose?.();
      } else {
        notification.error({
          message: "Error in sending appointment reminder",
          description: response.error,
          placement: "bottomRight",
        });
      }
    }
  };
  return (
    <div className={`${className}`} onClick={(e) => e.stopPropagation()}>
      {HeaderComponent ? (
        <HeaderComponent />
      ) : (
        <div className="HeadingsH2Bold text-center dr-p-16">Send Reminder</div>
      )}
      <div className="dr-w-full dr-h-1 dr-bg-doctor-darwin-neutral-100" />
      <BookedDetailsCard StyledIcon={UserRegularIcon}>
        <div className="flex items-center space-x-8 dr-px-13 dr-py-10 rounded-full border dr-border-doctor-darwin-accent-bright-blue-800 dr-max-w-min">
          <div className="dr-text-darwin-touch-neutral-1000 dr-min-w-max">
            {patient.fln}
          </div>
          {(comm_meta?.length || 0) > 0 ? (
            <div className="flex items-center space-x-4 dr-text-doctor-darwin-accent-bright-yellow-900 dr-bg-doctor-darwin-accent-bright-yellow-200 dr-rounded-6 dr-px-5">
              <BellRegularIcon />
              <div className="space-x-4">
                <span>{comm_meta?.length.toString().padStart(2, "0")}</span>
                <span>sent</span>
              </div>
            </div>
          ) : (
            <></>
          )}
        </div>
      </BookedDetailsCard>
      <div className="dr-w-full dr-h-1 dr-bg-doctor-darwin-neutral-100" />
      <BookedDetailsCard StyledIcon={CalendarRegularIcon}>
        <div className="dr-text-darwin-touch-neutral-1000 HeadingsH2Regular">
          Now
        </div>
      </BookedDetailsCard>
      <div className="dr-w-full dr-h-1 dr-bg-doctor-darwin-neutral-100" />
      <BookedDetailsCard StyledIcon={MessageRegularIcon} alignIconStart>
        {getReminderMessage(
          patient.fln,
          doctorFullName || "Doctor",
          new Date(start_time),
          queueType
        )}
      </BookedDetailsCard>
      <div className="dr-w-full dr-h-1 dr-bg-doctor-darwin-neutral-100" />
      <div className="p-16">
        <IconButton
          activeClass="dr-bg-darwin-touch-primary-primary hover:dr-bg-darwin-touch-primary-dark hover:dr-border-darwin-touch-primary-dark focus:dr-bg-darwin-touch-primary-dark focus:dr-border-darwin-touch-primary-dark active:dr-bg-darwin-touch-primary-dark active:dr-border-darwin-touch-primary-dark dr-border dr-border-darwin-touch-primary-primary"
          disabledClass="dr-bg-darwin-touch-neutral-300 dr-border-darwin-touch-neutral-300 dr-cursor-not-allowed"
          className="flex items-center space-x-8 dr-rounded-8 dr-w-full dr-h-40 dr-min-h-40 justify-center focus:dr-text-doctor-darwin-neutral-0 active:dr-text-doctor-darwin-neutral-0  hover:dr-text-doctor-darwin-neutral-0 dr-text-doctor-darwin-neutral-0 BodyBody2Regular"
          icon={<BellRegularIcon className="w-24 dr-max-w-24" />}
          loading={isLoading}
          onClick={handleFollowupReminder}
        >
          <div className="">Send Reminder</div>
        </IconButton>
      </div>
    </div>
  );
};

export default SendReminder;
